import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useUpdateProject, type Project, type ProjectPatch } from '@/api/projects';

export interface EditProjectModalProps {
  project: Project;
  opened: boolean;
  onClose: () => void;
}

/** Staff edit of a project's descriptive fields; only what changed is sent. Mount with a fresh key per opening.
 * Internal notes are no field here: they are the project's internal comments, on its page. */
export function EditProjectModal({ project, opened, onClose }: EditProjectModalProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const update = useUpdateProject(project.public_id);
  const [name, setName] = useState(project.name);
  const [crs, setCrs] = useState(project.crs ?? '');
  const [acq, setAcq] = useState(project.acquisition_date ?? '');
  const [acqEnd, setAcqEnd] = useState(project.acquisition_period_end ?? '');
  const [error, setError] = useState<string | null>(null);

  const patch: ProjectPatch = {};
  if (name !== project.name) patch.name = name;
  if (crs !== (project.crs ?? '')) patch.crs = crs;
  if (acq !== (project.acquisition_date ?? '')) patch.acquisition_date = acq || null;
  if (acqEnd !== (project.acquisition_period_end ?? '')) patch.acquisition_period_end = acqEnd || null;
  const dirty = Object.keys(patch).length > 0;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync(patch);
      notifications.show({ color: 'forest', message: t('edit.saved') });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('edit.title')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput label={t('project.name')} required withAsterisk={false} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput label={t('project.crs')} description={t('edit.crsHint')} value={crs} onChange={(e) => setCrs(e.currentTarget.value)} placeholder="EPSG:5514" />
          <Group grow>
            <TextInput label={t('project.acquisitionDate')} type="date" value={acq} onChange={(e) => setAcq(e.currentTarget.value)} />
            <TextInput label={t('project.acquisitionPeriodEnd')} type="date" value={acqEnd} onChange={(e) => setAcqEnd(e.currentTarget.value)} />
          </Group>
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {tc('common.cancel')}
            </Button>
            <Button type="submit" loading={update.isPending} disabled={!dirty}>
              {tc('common.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
